import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Users, TrendingUp, TrendingDown, Minus, Clock, Target, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TENDANCE_CONFIG = {
  amelioration: { label: 'En amelioration', icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
  stable: { label: 'Stable', icon: Minus, color: 'text-slate-500', bg: 'bg-slate-50' },
  degradation: { label: 'En baisse', icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50' },
};

function ScoreCircle({ score }) {
  const color = score >= 75 ? '#059669' : score >= 50 ? '#d97706' : '#dc2626';
  const circumference = 2 * Math.PI * 32;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r="32" fill="none" stroke="#e5e7eb" strokeWidth="5" />
        <circle cx="36" cy="36" r="32" fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold" style={{ color }}>{score}</span>
        <span className="text-[9px] text-slate-400">/100</span>
      </div>
    </div>
  );
}

export function ScoreFiabiliteChefs() {
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChef, setExpandedChef] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/crm/fiabilite-chefs`);
        if (res.status === 200) {
          const data = res.data;
          setChefs(data.chefs || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center text-slate-400 py-8">Chargement des analyses...</div>;
  if (chefs.length === 0) return <div className="text-center text-slate-400 py-8">Aucun chantier termine pour analyser.</div>;

  return (
    <div className="space-y-3" data-testid="score-fiabilite-section">
      {chefs.map(chef => {
        const tendCfg = TENDANCE_CONFIG[chef.tendance] || TENDANCE_CONFIG.stable;
        const TendIcon = tendCfg.icon;
        const isExpanded = expandedChef === chef.chef_id;
        const deltaLabel = chef.moyenne_delta_jours < 0
          ? `${Math.abs(chef.moyenne_delta_jours)}j en avance`
          : chef.moyenne_delta_jours > 0
            ? `${chef.moyenne_delta_jours}j en retard`
            : 'Pile a temps';
        const deltaColor = chef.moyenne_delta_jours <= 0 ? 'text-green-600' : 'text-red-600';

        return (
          <Card key={chef.chef_id} className="border" data-testid={`fiabilite-chef-${chef.chef_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <ScoreCircle score={chef.score_global} />

                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-indigo-600" />
                        {chef.chef_nom}
                      </h3>
                      <p className="text-xs text-slate-400">{chef.nb_chantiers_termines} chantier(s) termine(s)</p>
                    </div>
                    <Badge className={`text-[10px] ${tendCfg.bg} ${tendCfg.color} border-0`}>
                      <TendIcon className="w-3 h-3 mr-1" />
                      {tendCfg.label}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Target className="w-3 h-3 text-slate-400" />
                        <span className="text-xs font-semibold">{chef.taux_respect_delais}%</span>
                      </div>
                      <p className="text-[10px] text-slate-400">Respect delais</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className={`text-xs font-semibold ${deltaColor}`}>{deltaLabel}</span>
                      </div>
                      <p className="text-[10px] text-slate-400">Moy. delta</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <BarChart3 className="w-3 h-3 text-slate-400" />
                        <span className="text-xs font-semibold">
                          {chef.moyenne_efficacite_heures ? `${chef.moyenne_efficacite_heures}%` : '—'}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-400">Eff. heures</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggle historique */}
              <button
                className="w-full mt-2 flex items-center justify-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
                onClick={() => setExpandedChef(isExpanded ? null : chef.chef_id)}
                data-testid={`toggle-historique-${chef.chef_id}`}
              >
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {isExpanded ? 'Masquer' : 'Voir'} les derniers chantiers
              </button>

              {isExpanded && chef.derniers_chantiers?.length > 0 && (
                <div className="mt-2 space-y-1 border-t pt-2">
                  {chef.derniers_chantiers.map(bon => {
                    const bonDeltaColor = bon.delta_jours <= 0 ? 'bg-green-50 text-green-700' : bon.delta_jours <= 2 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700';
                    return (
                      <div key={bon.bon_id} className="flex items-center justify-between text-xs py-1">
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium">{bon.client_nom}</p>
                          <p className="text-[10px] text-slate-400 truncate">{bon.nom_dossier} | {bon.type_travaux === 'exterieur' ? 'Ext.' : 'Int.'}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          {bon.heures_budget > 0 && (
                            <span className="text-[10px] text-slate-400">{bon.heures_punchees}h/{bon.heures_budget}h</span>
                          )}
                          <Badge className={`text-[10px] ${bonDeltaColor} border-0`}>
                            {bon.delta_jours < 0 ? `${Math.abs(bon.delta_jours)}j avance` : bon.delta_jours > 0 ? `${bon.delta_jours}j retard` : 'A temps'}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
